import Axios from "axios";
import { Config } from "./config";
import {
  addBreadcrumb,
  captureException,
  logApiFailure,
  sanitizeForSentry,
} from "./sentry";

const axiosInstance = Axios.create({
  baseURL: Config.baseURL,
  headers: { "Content-Type": "application/json", "Platform-Type": "web" },
});

const safeParseBody = (data) => {
  if (!data) return undefined;
  if (typeof data === "string") {
    try {
      return sanitizeForSentry(JSON.parse(data));
    } catch (e) {
      // form-urlencoded / raw — only keep length & redacted preview
      return {
        type: "raw",
        length: data.length,
        preview: data.slice(0, 120).replace(/card\[[^\]]+\]=[^&]*/gi, "card[*]=[REDACTED]"),
      };
    }
  }
  return sanitizeForSentry(data);
};

axiosInstance.interceptors.request.use(
  (config) => {
    config.metadata = { startTime: Date.now() };

    addBreadcrumb({
      category: "http",
      type: "http",
      level: "info",
      data: {
        method: config.method,
        url: config.url,
        baseURL: config.baseURL,
        params: sanitizeForSentry(config.params),
        body: safeParseBody(config.data),
      },
      message: `${(config.method || "get").toUpperCase()} ${config.url}`,
    });
    return config;
  },
  (error) => {
    captureException(error, { source: "axios.request" });
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => {
    const durationMs =
      Date.now() - (response?.config?.metadata?.startTime || Date.now());

    addBreadcrumb({
      category: "http",
      type: "http",
      level: "info",
      data: {
        method: response?.config?.method,
        url: response?.config?.url,
        status: response?.status,
        durationMs,
        // Keep response shape/flags, not huge payloads
        value: response?.data?.value,
        hasData: response?.data?.data !== undefined,
        message:
          response?.data?.message ||
          response?.data?.data?.message ||
          undefined,
      },
      message: `HTTP ${response?.status} ${(response?.config?.method || "").toUpperCase()} ${response?.config?.url}`,
    });

    // Business-level API failure returned as HTTP 200 with value:false
    if (response?.data && response.data.value === false) {
      addBreadcrumb({
        category: "api.business",
        level: "warning",
        message: `API value=false ${response.config?.url}`,
        data: sanitizeForSentry({
          url: response.config?.url,
          method: response.config?.method,
          error: response.data?.error,
          message: response.data?.message || response.data?.error?.message,
        }),
      });
    }

    return response;
  },
  (error) => {
    const durationMs =
      Date.now() - (error?.config?.metadata?.startTime || Date.now());
    const status = error?.response?.status;
    const url = error?.config?.url;
    const method = error?.config?.method;

    addBreadcrumb({
      category: "http",
      type: "http",
      level: "error",
      data: {
        method,
        url,
        status,
        durationMs,
        code: error?.code,
        response: sanitizeForSentry(error?.response?.data),
      },
      message: `HTTP ${status || "error"} ${method || ""} ${url || ""}`,
    });

    if (error?.code !== "ERR_CANCELED") {
      logApiFailure(error, {
        source: "axios.response",
        feature: "http",
        durationMs,
      });
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
